import React, { useEffect, useState, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import Header from "./Visuals/header";
import Footer from "./Visuals/footer";
import Sidebar from "./Conversations/sidebar";

const Landing = lazy(() => import("./Visuals/landing"));
const Mission = lazy(() => import("./Visuals/mission"));
const Team = lazy(() => import("./Visuals/team"));
const Privacy = lazy(() => import("./Visuals/privacy"));
const Terms = lazy(() => import("./Visuals/terms"));
const Profile = lazy(() => import("./Visuals/profile"));

const Chat = lazy(() => import("./Conversations/chat"));
const Questionnaires = lazy(() => import("./Conversations/questionnaires"));
const QuestionnairesPage = lazy(() => import("./Conversations/specificQuestionnaires"));
const Suggestions = lazy(() => import("./Conversations/suggestions"));

const AuthPage = lazy(() => import("./AccountSetup/logIn"));
const ForgotPassword = lazy(() => import("./AccountSetup/forgotPassword"));
const SignUpPage = lazy(() => import("./AccountSetup/signUpPage"));
const ResetPassword = lazy(() => import("./AccountSetup/resetPassword"));

function AppWrapper() {
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsub();
  }, []);

  const currentPath = location.pathname.toLowerCase().replace(/\/+$/, "");

  const showSidebarRoutes = ["/chat", "/questionnaires", "/questionnaire", "/suggestions"];
  const hideHeaderFooterRoutes = [
    "/authpage",
    "/signup",
    "/forgot-password",
    "/reset-password",
    "/chat",
    "/questionnaires",
    "/questionnaire",
    "/suggestions",
    "/checkout",
  ];

  const hideHeaderFooter = hideHeaderFooterRoutes.some((p) => currentPath.startsWith(p));
  const showSidebar = showSidebarRoutes.some((p) => currentPath.startsWith(p));

  useEffect(() => {
    const scaleRoutes = ["/chat", "/suggestions", "/questionnaires", "/questionnaire"];
    const shouldScale = scaleRoutes.some((p) => currentPath.startsWith(p));

    const applyViewport = (content) => {
      let oldViewport = document.querySelector("meta[name=viewport]");
      if (oldViewport) oldViewport.remove();

      const newViewport = document.createElement("meta");
      newViewport.name = "viewport";
      newViewport.content = content;
      document.head.appendChild(newViewport);
    };

    if (shouldScale) {
      applyViewport(
        "width=device-width, initial-scale=0.7, maximum-scale=0.7, user-scalable=no, viewport-fit=cover"
      );
    } else {
      applyViewport(
        "width=device-width, initial-scale=0.35, maximum-scale=0.35, user-scalable=no, viewport-fit=cover"
      );
    }
  }, [currentPath]);

  return (
    <div className="app-outer">
      {!hideHeaderFooter && <Header user={user} />}
      <div className="app-inner main-wrapper">
        {showSidebar && <Sidebar />}
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Landing user={user} />} />
            <Route path="/mission" element={<Mission user={user} />} />
            <Route path="/team" element={<Team />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/profile" element={<Profile user={user} />} />

            <Route path="/authpage" element={<AuthPage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/chat" element={<Chat user={user} />} />
            <Route path="/questionnaires" element={<Questionnaires />} />
            <Route path="/questionnaire/:id" element={<QuestionnairesPage />} />
            <Route path="/suggestions" element={<Suggestions />} />

            <Route path="*" element={<div style={{ padding: 24 }}>Not found</div>} />
          </Routes>
        </main>
      </div>
      {!hideHeaderFooter && <Footer />}
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AppWrapper />
    </Router>
  );
}
